/**
 * Innovations API client
 * Connects frontend to Registry service backend
 */

import apiClient from './auth';
import { getAuthToken } from '../utils/auth';
import { API_BASE_URL } from '@/config/apiBase';

export interface InnovationSubmission {
  submission_uuid: string;
  title: string;
  abstract: string;
  current_state: 'idea' | 'exploration' | 'concept' | 'prototype' | 'demo';
  status: 'draft' | 'submitted' | 'under_review' | 'completed' | 'rejected';
  submission_date?: string;
  last_updated?: string;
  /** Present in list responses; used to enforce client-side visibility (only show own for innovators) */
  submitter_uuid?: string;
  institution_uuid?: string | null;
  keywords?: string[];
  technology_area?: string;
  expected_outcome?: string;
  /** What makes the IP unique (novelty / differentiation) */
  ip_uniqueness?: string | null;
  documents?: Document[];
  kth_prompts?: {
    level: number;
    questions: string[];
  };
  /** Set when loading a single submission (e.g. GET /innovations/:uuid) */
  submitter_email?: string;
  institution_name?: string;
  review_decision?: 'has-potential' | 'no-potential';
  recommended_ip_types?: string[];
  ip_protection_recommendation?: string;
  filing_instructions?: string;
  /** Optional HTTPS link to the full written report (e.g. shared doc); visible to stakeholders like other feedback. */
  comprehensive_report_url?: string | null;
  reviewed_at?: string;
  feedback_approval_status?: 'pending_superadmin_review' | 'approved' | 'changes_requested';
  feedback_rejection_reason?: string;
  feedback_approved_at?: string;
  filing_request_selected_types?: string[];
  filing_request_notified_to_reviewer?: boolean;
  filing_request_notified_at?: string;
  filing_request_status?: 'none' | 'received' | 'in_support' | 'ready_to_file' | 'closed';
  filing_request_received_at?: string;
  filing_request_in_support_at?: string;
  filing_request_ready_at?: string;
  filing_request_closed_at?: string;
  filing_transition_requested_status?: 'in_support' | 'ready_to_file' | 'closed';
  filing_transition_requested_at?: string;
  filing_transition_rejection_reason?: string;
  filing_request?: {
    selected_types: string[];
    notified_to_reviewer: boolean;
    notification_date?: string;
    status?: 'none' | 'received' | 'in_support' | 'ready_to_file' | 'closed';
    received_at?: string;
    in_support_at?: string;
    ready_at?: string;
    closed_at?: string;
    transition_requested_status?: 'in_support' | 'ready_to_file' | 'closed';
    transition_requested_at?: string;
    transition_rejection_reason?: string;
  } | null;
  /** Insolip-prepared filing documentation; separate offline fee; super admin confirms payment and publishes link */
  filing_documentation_prep?: {
    requested_at?: string | null;
    /** Individual (non-institution) submissions: set by super admin before the innovator may request prep */
    billing_cleared_at?: string | null;
    /** Legacy / admin-only; not used for institution flows in the UI */
    payment_confirmed_at?: string | null;
    package_ready_at?: string | null;
    package_url?: string | null;
  } | null;
}

export interface Document {
  doc_uuid: string;
  document_type: string;
  file_name: string;
  file_size: number;
  upload_date: string;
}

export interface CreateSubmissionData {
  title: string;
  abstract: string;
  current_state: 'idea' | 'exploration' | 'concept' | 'prototype' | 'demo';
  keywords?: string[];
  technology_area?: string;
  expected_outcome?: string;
  ip_uniqueness: string;
}

export interface SubmissionListResponse {
  submissions: InnovationSubmission[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Create new innovation submission (draft)
 */
export async function createSubmission(data: CreateSubmissionData): Promise<InnovationSubmission> {
  const response = await apiClient.post<{ message: string; submission: InnovationSubmission }>(
    '/innovations',
    data
  );
  return response.data.submission;
}

/**
 * Update innovation submission (draft)
 */
export async function updateSubmission(
  uuid: string,
  data: Partial<CreateSubmissionData>
): Promise<InnovationSubmission> {
  const response = await apiClient.patch<{ message: string; submission: InnovationSubmission }>(
    `/innovations/${uuid}`,
    data
  );
  return response.data.submission;
}

/**
 * Get submission details
 */
export async function getSubmission(uuid: string): Promise<InnovationSubmission> {
  const response = await apiClient.get<{ submission: InnovationSubmission }>(`/innovations/${uuid}`);
  return response.data.submission;
}

/**
 * List user's submissions (paginated)
 */
export async function listSubmissions(
  page: number = 1,
  limit: number = 20,
  status?: string
): Promise<SubmissionListResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    _t: Date.now().toString(),
  });
  
  if (status) {
    params.append('status', status);
  }

  const response = await apiClient.get<SubmissionListResponse>(`/innovations?${params.toString()}`);
  return response.data;
}

/** Read a File as base64 string (data URL prefix stripped). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      resolve(base64 || '');
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Upload supporting documents.
 * Uses JSON (base64) so uploads work when the reverse proxy strips or breaks multipart/form-data.
 */
export async function uploadDocuments(
  submissionUuid: string,
  files: File[]
): Promise<{ message: string; documents: Document[] }> {
  if (files.length === 0) {
    throw new Error('No files provided');
  }
  const documents = await Promise.all(
    files.map(async (file) => ({
      fileName: file.name,
      contentBase64: await fileToBase64(file),
    }))
  );

  const base = API_BASE_URL.replace(/\/$/, '');
  const url = `${base}/innovations/${submissionUuid}/documents/json`;
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ documents }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw Object.assign(new Error(data?.message || data?.error || `Upload failed: ${response.status}`), {
      response: { status: response.status, data },
    });
  }
  return data as { message: string; documents: Document[] };
}

/**
 * Download a supporting document (reviewers and admin/super_admin only).
 * Triggers a file download in the browser.
 */
export async function downloadDocument(
  submissionUuid: string,
  docUuid: string,
  fileName: string
): Promise<void> {
  const response = await apiClient.get<Blob>(
    `/innovations/${submissionUuid}/documents/${docUuid}`,
    { responseType: 'blob' }
  );
  const blob = response.data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'document';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Reviewer assignment (from GET /innovations/reviewer/my-assignments) */
export interface ReviewerAssignment {
  assignment_uuid: string;
  submission: {
    submission_uuid: string;
    title: string;
    abstract: string;
    current_state: string;
    technology_area?: string;
    keywords?: string[];
    submission_date?: string;
    feedback_approval_status?: 'pending_superadmin_review' | 'approved' | 'changes_requested';
    feedback_rejection_reason?: string;
    filing_request_selected_types?: string[];
    filing_request_notified_to_reviewer?: boolean;
    filing_request_notified_at?: string;
    filing_request_status?: 'none' | 'received' | 'in_support' | 'ready_to_file' | 'closed';
    filing_request_received_at?: string;
    filing_request_in_support_at?: string;
    filing_request_ready_at?: string;
    filing_request_closed_at?: string;
    filing_transition_requested_status?: 'in_support' | 'ready_to_file' | 'closed';
    filing_transition_requested_at?: string;
    filing_transition_rejection_reason?: string;
    documents: { doc_uuid: string; document_type: string; file_name: string }[];
  };
  assignment_details: {
    assigned_at: string;
    assignment_status: 'pending' | 'accepted' | 'declined' | 'in_progress' | 'completed';
    priority?: string;
    due_date?: string | null;
    days_remaining?: number | null;
    admin_notes?: string | null;
  };
}

export async function getMyAssignments(params?: { status?: string; sort_by?: string }): Promise<{ assignments: ReviewerAssignment[] }> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.sort_by) search.set('sort_by', params.sort_by);
  const q = search.toString();
  const response = await apiClient.get<{ assignments: ReviewerAssignment[] }>(
    `/innovations/reviewer/my-assignments${q ? `?${q}` : ''}`
  );
  return response.data;
}

export async function acceptAssignment(assignmentUuid: string): Promise<{ message: string; assignment_status: string }> {
  const response = await apiClient.post<{ message: string; assignment_status: string }>(
    `/innovations/reviewer/assignments/${assignmentUuid}/accept`
  );
  return response.data;
}

export async function declineAssignment(assignmentUuid: string, declineReason?: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    `/innovations/reviewer/assignments/${assignmentUuid}/decline`,
    declineReason != null ? { decline_reason: declineReason } : undefined
  );
  return response.data;
}

export async function submitReviewerAssessment(
  submissionUuid: string,
  payload: {
    decision: 'has-potential' | 'no-potential';
    ip_protection_types: string[];
    ip_protection_recommendation: string;
    filing_instructions?: string;
    comprehensive_report_url?: string | null;
  }
): Promise<{ message: string; status: string; submission_uuid: string }> {
  const response = await apiClient.post<{ message: string; status: string; submission_uuid: string }>(
    `/innovations/reviewer/submissions/${submissionUuid}/complete`,
    payload
  );
  return response.data;
}

/**
 * Finalize submission (draft → submitted)
 */
export async function finalizeSubmission(uuid: string): Promise<InnovationSubmission> {
  const response = await apiClient.post<{ message: string; submission: InnovationSubmission }>(
    `/innovations/${uuid}/submit`
  );
  return response.data.submission;
}

export async function notifyReviewerForFiling(
  submissionUuid: string,
  selectedTypes: string[]
): Promise<{ message: string; submission_uuid: string; filing_request: { selected_types: string[]; notified_to_reviewer: boolean } }> {
  const response = await apiClient.post<{
    message: string;
    submission_uuid: string;
    filing_request: { selected_types: string[]; notified_to_reviewer: boolean };
  }>(
    `/innovations/${submissionUuid}/notify-reviewer-filing`,
    { selected_types: selectedTypes }
  );
  return response.data;
}

/** Request Insolip to prepare filing documentation (fee agreed and paid outside the app; super admin confirms here). */
export async function requestFilingDocumentationPrep(
  submissionUuid: string
): Promise<{ message: string; submission_uuid: string }> {
  const response = await apiClient.post<{ message: string; submission_uuid: string }>(
    `/innovations/${submissionUuid}/request-filing-documentation`
  );
  return response.data;
}

export async function updateFilingRequestStatus(
  submissionUuid: string,
  status: 'in_support' | 'ready_to_file' | 'closed'
): Promise<{ message: string; submission_uuid: string; filing_request_status: string }> {
  const response = await apiClient.post<{ message: string; submission_uuid: string; filing_request_status: string }>(
    `/innovations/reviewer/submissions/${submissionUuid}/filing-request/status`,
    { status }
  );
  return response.data;
}

/**
 * Bulk upload innovations (Excel/CSV + ZIP).
 * Uses fetch without setting Content-Type so the browser sets multipart/form-data with boundary (avoids proxy issues).
 */
export async function bulkUpload(
  dataFile: File,
  documentsArchive?: File
): Promise<{
  message: string;
  batch_uuid: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errorReportPath?: string;
  statusUrl: string;
}> {
  const formData = new FormData();
  formData.append('dataFile', dataFile);
  if (documentsArchive) {
    formData.append('documentsArchive', documentsArchive);
  }

  const base = API_BASE_URL.replace(/\/$/, '');
  const token = getAuthToken();
  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
  };
  // Do not set Content-Type: fetch will set multipart/form-data with boundary

  const response = await fetch(`${base}/innovations/bulk/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw Object.assign(
      new Error(data?.message || data?.error || `Bulk upload failed: ${response.status}`),
      { response: { status: response.status, data } }
    );
  }
  return data;
}

/**
 * Get bulk batch status
 */
export async function getBulkBatchStatus(batchUuid: string): Promise<{
  batch: {
    batch_uuid: string;
    institution_uuid: string;
    total_submissions: number;
    processed_count: number;
    failed_count: number;
    status: string;
    uploaded_at: string;
    error_report_path?: string;
    jobStatus?: {
      state: string;
      progress: number;
      attemptsMade: number;
      failedReason?: string;
    };
    errorReportUrl?: string;
  };
}> {
  const response = await apiClient.get(`/innovations/bulk/batches/${batchUuid}/status`);
  return response.data;
}

/**
 * List bulk batches
 */
export async function listBulkBatches(
  page: number = 1,
  limit: number = 20
): Promise<{
  batches: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}> {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  const response = await apiClient.get(`/innovations/bulk/batches?${params.toString()}`);
  return response.data;
}
